import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button, TextInput, Select } from "@/components";
import { X, Plus, Upload } from "lucide-react";
import type {
  Campaign,
  CreateCampaignParams,
  UpdateCampaignParams,
  CampaignConfig,
  CampaignDurationPeriod,
} from "../interfaces";

const DURATION_PERIOD_OPTIONS: { value: CampaignDurationPeriod; label: string }[] =
  [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

const campaignSchema = yup.object({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  period: yup.string().oneOf(["daily", "weekly", "monthly", "quarterly", "yearly"]).optional(),
  periodCount: yup.number().min(1, "Must be at least 1").optional(),
  goals: yup.array().of(yup.string()).optional(),
  cta: yup
    .array()
    .of(
      yup.object({
        url: yup.string().url("Must be a valid URL").required("URL is required"),
        goal: yup.string().required("Goal is required"),
      })
    )
    .optional(),
  faqs: yup
    .array()
    .of(
      yup.object({
        question: yup.string().required("Question is required"),
        answer: yup.string().required("Answer is required"),
      })
    )
    .optional(),
});

type CampaignFormData = {
  title: string;
  description: string;
  period: CampaignDurationPeriod;
  periodCount: number;
  goals: string[];
  cta: { url: string; goal: string }[];
  faqs: { question: string; answer: string }[];
};

interface CampaignFormProps {
  campaign?: Campaign;
  onSubmit: (data: CreateCampaignParams | UpdateCampaignParams) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const defaultConfig: CampaignFormData = {
  title: "",
  description: "",
  period: "weekly",
  periodCount: 1,
  goals: [],
  cta: [],
  faqs: [],
};

export const CampaignForm: React.FC<CampaignFormProps> = ({
  campaign,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CampaignFormData>({
    resolver: yupResolver(campaignSchema) as any,
    defaultValues: campaign
      ? {
          title: campaign.title,
          description: campaign.description,
          period: campaign.config?.duration?.period ?? "weekly",
          periodCount: campaign.config?.duration?.periodCount ?? 1,
          goals: campaign.config?.goals ?? [],
          cta: campaign.config?.cta ?? [],
          faqs: campaign.config?.faqs ?? [],
        }
      : { ...defaultConfig },
  });

  const {
    fields: goalFields,
    append: appendGoal,
    remove: removeGoal,
  } = useFieldArray({
    control,
    // @ts-expect-error FieldArrayPath for string[] - works at runtime
    name: "goals",
  });

  const {
    fields: ctaFields,
    append: appendCta,
    remove: removeCta,
  } = useFieldArray({
    control,
    name: "cta",
  });

  const {
    fields: faqFields,
    append: appendFaq,
    remove: removeFaq,
  } = useFieldArray({
    control,
    name: "faqs",
  });

  useEffect(() => {
    if (campaign?.images) {
      setImagePreviews(campaign.images);
    }
  }, [campaign]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 10) {
      alert("Maximum 10 images allowed");
      return;
    }
    setImageFiles(files);
    const previews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setVideoFiles(files);
  };

  const onFormSubmit = async (data: CampaignFormData) => {
    const config: CampaignConfig = {
      cta: (data.cta || []).filter((c) => c.url && c.goal),
      faqs: data.faqs || [],
      duration: {
        period: data.period,
        periodCount: data.periodCount,
      },
      goals: (data.goals || []).filter((g): g is string => typeof g === "string" && g.trim().length > 0),
    };

    const baseParams = {
      title: data.title,
      description: data.description,
      config,
      images: imageFiles.length > 0 ? imageFiles : undefined,
      videoFiles: videoFiles.length > 0 ? videoFiles : undefined,
    };

    if (campaign) {
      await onSubmit({
        id: campaign.id,
        ...baseParams,
      } as UpdateCampaignParams);
    } else {
      await onSubmit(baseParams as CreateCampaignParams);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <TextInput
        label="Title"
        placeholder="Enter campaign title"
        error={!!errors.title}
        helperText={errors.title?.message}
        {...register("title")}
      />

      <div>
        <label className="text-sm font-medium mb-2 block">Description</label>
        <textarea
          className="w-full min-h-[100px] px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="Enter campaign description"
          {...register("description")}
        />
        {errors.description && (
          <p className="text-xs text-destructive mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Duration */}
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Duration Period"
          options={DURATION_PERIOD_OPTIONS}
          value={watch("period")}
          onChange={(val) => setValue("period", val as CampaignDurationPeriod)}
        />
        <TextInput
          label="Duration Count"
          type="number"
          min={1}
          error={!!errors.periodCount}
          helperText={errors.periodCount?.message}
          {...register("periodCount", { valueAsNumber: true })}
        />
      </div>

      {/* Goals */}
      <div>
        <label className="text-sm font-medium mb-2 block">Goals</label>
        {goalFields.map((field, index) => (
          <div key={field.id} className="flex gap-2 mb-2">
            <TextInput
              placeholder="Enter goal"
              className="flex-1"
              {...register(`goals.${index}` as const)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeGoal(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendGoal("" as any)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Goal
        </Button>
      </div>

      {/* CTAs */}
      <div>
        <label className="text-sm font-medium mb-2 block">Call to Action</label>
        {ctaFields.map((field, index) => (
          <div key={field.id} className="border border-input rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">CTA {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCta(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <TextInput
              label="Goal"
              placeholder="e.g. Sign up now"
              className="mb-2"
              {...register(`cta.${index}.goal` as const)}
            />
            <TextInput
              label="URL"
              placeholder="https://example.com"
              error={!!errors.cta?.[index]?.url}
              helperText={errors.cta?.[index]?.url?.message}
              {...register(`cta.${index}.url` as const)}
            />
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendCta({ url: "", goal: "" })}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add CTA
        </Button>
      </div>

      {/* FAQs */}
      <div>
        <label className="text-sm font-medium mb-2 block">FAQs</label>
        {faqFields.map((field, index) => (
          <div key={field.id} className="border border-input rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">FAQ {index + 1}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeFaq(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <TextInput
              label="Question"
              placeholder="Enter question"
              className="mb-2"
              {...register(`faqs.${index}.question` as const)}
            />
            <div>
              <label className="text-sm font-medium mb-2 block">Answer</label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="Enter answer"
                {...register(`faqs.${index}.answer` as const)}
              />
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => appendFaq({ question: "", answer: "" })}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* Images */}
      <div>
        <label className="text-sm font-medium mb-2 block">Images</label>
        <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-input rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="flex flex-col items-center">
            <Upload className="h-6 w-6 text-muted-foreground mb-2" />
            <span className="text-sm text-muted-foreground">
              Click to upload images (max 10)
            </span>
          </div>
          <input
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </label>
        {imagePreviews.length > 0 && (
          <div className="grid grid-cols-4 gap-4 mt-4">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-1 right-1 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Videos */}
      <div>
        <label className="text-sm font-medium mb-2 block">Videos</label>
        <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-input rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="flex flex-col items-center">
            <Upload className="h-6 w-6 text-muted-foreground mb-2" />
            <span className="text-sm text-muted-foreground">
              Click to upload videos
            </span>
          </div>
          <input
            type="file"
            multiple
            accept="video/*"
            className="hidden"
            onChange={handleVideoChange}
          />
        </label>
        {videoFiles.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {videoFiles.length} video{videoFiles.length > 1 ? "s" : ""} selected
          </p>
        )}
      </div>

      <div className="flex gap-4 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? "Saving..." : campaign ? "Update Campaign" : "Create Campaign"}
        </Button>
      </div>
    </form>
  );
};
